import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { InMemoryTokenStore, Logger } from '@se/se.ui.html5';
import { catchError, exhaustMap, filter, firstValueFrom, forkJoin, from, of, switchMap, take, tap } from 'rxjs';

import { EstensaNotificationService } from '../services/angularjs/estensa-notification.service';
import { ImagingService } from '../services/imaging/imaging.service';
import { WpfService } from '../connectivity/wpf.service';
import { RadioSettingsService } from '../services/settings/radio-settings.service';
import { UserConfigurationSettingsService } from '../services/settings/user-configuration-settings.service';
import { seLogin, seNotificationError, seNotificationSuccess, seNotificationWarning } from './root.actions';
import { ExtensaService } from '../connectivity/extensa.service';
import { CpHttpService } from '../custom-pages/services/cp-http.service';
import { SignalrNotificationsService } from '../services/signalr-notifications.service';
import { IdleCheckerService } from '../services/idle-checker/idle-checker.service';
import { AuthenticationSSOService } from '../services/angularjs/authentication-sso.service';
import { Html5SSOFlowType } from 'estensa-module';
import { TpIntegrationsService } from '../services/tp-integrations/tp-integrations.service';
import { SecondaryApplicationSettingsService, SsoLoginService } from '@se/platform';

@Injectable()
export class RootEffects {
    login$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seLogin),
                exhaustMap(({ sso }) => from(this.onLogin(sso))),
            ),
        { dispatch: false },
    );

    notificationError$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seNotificationError),
                tap(({ message }) => {
                    this.notificationService.error(message);
                }),
            ),
        { dispatch: false },
    );

    notificationSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seNotificationSuccess),
                tap(({ message }) => {
                    this.notificationService.success(message);
                }),
            ),
        { dispatch: false },
    );

    notificationWarning$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seNotificationWarning),
                tap(({ message }) => {
                    this.notificationService.warning(message);
                }),
            ),
        { dispatch: false },
    );

    constructor(
        private actions$: Actions,
        private authenticationSSO: AuthenticationSSOService,
        private cpHttp: CpHttpService,
        private imaging: ImagingService,
        private extensaService: ExtensaService,
        private idleChecker: IdleCheckerService,
        private logger: Logger,
        private notificationService: EstensaNotificationService,
        private radioSettingsService: RadioSettingsService,
        private secondaryApplicationSettingsService: SecondaryApplicationSettingsService,
        private signalrNotificationsService: SignalrNotificationsService,
        private ssoLoginService: SsoLoginService,
        private tokenStore: InMemoryTokenStore,
        private tpIntegrations: TpIntegrationsService,
        private userConfigurationService: UserConfigurationSettingsService,
        private wpf: WpfService,
    ) {}

    private async onLogin(sso: string): Promise<void | any[]> {
        try {
            // Servizi core
            const [_, radioSettings] = await Promise.all([
                this.userConfigurationService.initService(),
                this.radioSettingsService.initService(),
                this.secondaryApplicationSettingsService.initService(),
            ]);
            await this.extensaService.init();

            // Servizi dipendenti da connettività
            await Promise.all([this.imaging.initService(), this.wpf.initService()]);

            //Connessione a WPF
            if (sso === 'WPF') {
                await this.extensaService.startConnection(this.tokenStore.getToken());
            }

            if (radioSettings?.idleCheckSettings?.enabled) {
                this.idleChecker.enable(radioSettings.idleCheckSettings);
            }

            if (radioSettings.html5ConfigSettings.html5SsoSettings.flowType !== Html5SSOFlowType.Disabled) {
                await this.handleSSOLogin();
            }

            this.preloadCustomPageItemSources();

            await this.signalrNotificationsService.getActiveAlarms(true);
        } catch (err) {
            this.logger.error(err.message);
        }
    }

    private async handleSSOLogin() {
        const { pt } = this.authenticationSSO.getQueryString();
        if (!pt) {
            return;
        }

        const data = await firstValueFrom(this.authenticationSSO.getPairingData(pt));
        const accessTokenResponse = JSON.parse(data.parameterDictionary.accessTokenResponse);

        this.ssoLoginService.setOpenIdToken(accessTokenResponse.id_token);
        this.tpIntegrations.isActionReady$
            .pipe(
                filter((isReady) => isReady),
                take(1),
            )
            .subscribe(() => {
                this.tpIntegrations.onCallAction({
                    action: 'REFRESH_TOKEN',
                    data: {
                        actionId: 'test-token',
                        refreshUri: 'client/api/sso/refresh',
                        cacheKey: 'sso-token',
                        cacheInitialValue: accessTokenResponse,
                        isReset: false,
                    },
                });
            });
    }

    private preloadCustomPageItemSources() {
        // Utilizziamo la chiamata solo per popolare la cache.
        // Non facciamo await perché vogliamo che succeda in background.
        this.cpHttp
            .getPreloadableLayouts()
            .pipe(
                switchMap((layouts) =>
                    forkJoin(
                        layouts
                            .filter((layout) => layout.isActive)
                            .sort((a, b) => b.order - a.order)
                            .slice(0, 3)
                            .map((l) =>
                                this.cpHttp
                                    .getItemSourcesWithItemsByIdCustomPage(l.id, true)
                                    .pipe(catchError(() => of(undefined))),
                            ),
                    ),
                ),
            )
            .subscribe();
    }
}
